<template>
  <div :class="[{ 'expand-vertical': !labelledActions }]">
    <p v-if="description" class="field-description">{{ description }}</p>
    <div
      :class="[
        'image-container',
        labelledActions ? 'fixed-height' : 'expand-vertical',
        { 'custom-error-border': validator && validator.$error },
      ]"
    >
      <drop class="expand-vertical" @drop="handleDrop">
        <div class="text-center p-2 expand-vertical">
          <v-layout
            v-if="!value"
            justify-center
            align-center
            row
            class="image-placeholder expand-vertical"
            @click.stop.prevent="openFSPicker()"
          >
            <v-flex v-if="uploadInProgress">
              <v-progress-circular color="primary" indeterminate :size="75" />
            </v-flex>
            <v-flex v-else>
              <v-layout column>
                <v-icon color="primary" class="icon-placeholder" large>photo</v-icon>
                <span class="text-placeholder">{{ adminText(&quot;shellAll.add_cover_image&quot;, &quot;Add cover image&quot;) }}</span>
                <span class="text-description">{{ adminText(&quot;shellAll.image_no_larger_than_10_mb_is_recommended&quot;, &quot;Image no larger than 10 MB is recommended.&quot;) }}</span>
              </v-layout>
            </v-flex>
          </v-layout>

          <v-layout
            v-else
            :style="{ backgroundImage: 'url(' + value + ')' }"
            justify-start
            align-end
            class="image-field expand-vertical"
          >
            <template v-if="!labelledActions">
              <v-flex shrink class="pr-8 ml-12 mb-12 image-button">
                <v-btn icon class="size-32 m-0 primary" @click.stop="openFSPicker">
                  <v-icon>photo_library</v-icon>
                </v-btn>
              </v-flex>
              <v-flex shrink class="mb-12 image-button">
                <v-btn icon class="size-32 m-0 error" @click.stop="removeImage">
                  <v-icon>delete</v-icon>
                </v-btn>
              </v-flex>
            </template>
          </v-layout>
        </div>
      </drop>
    </div>
    <v-layout v-if="labelledActions && value" column class="cover-actions pt-4">
      <main-button :text="adminText(&quot;shellAll.change_image&quot;, &quot;Change image&quot;)" depressed @click="openFSPicker" />
      <main-button :text="adminText(&quot;shellAll.delete&quot;, &quot;Delete&quot;)" color="error" depressed class="mt-2" @click="removeImage" />
    </v-layout>
    <div :class="['custom-error-message', { 'error--text': validator && validator.$error }]">
      <span> {{ getErrors(validator)[0] }} &nbsp;</span>
    </div>
    <cropper-dialog
      v-if="cropper"
      :aspect-ratio="3"
      :image="dataURLImage"
      @dismissed="removeImage"
      @cropped="uploadAvatar($event)"
    />
    <hidden-file-input ref="file-input" max-file-size-mb="10" @file="uploadAvatar($event)" />
  </div>
</template>

<script>
import AdminContext from '@/admin-i18n/context'
import CropperDialog from './../dialogs/cropper-dialog'
import HiddenFileInput from './../hidden-file-input'
import {
  fileToDataUrl,
  getUploadOptions,
  uploadImage,
  defaultMessages,
  getValidationErrors,
} from '@/utils'
import { UploadPaths } from '@/types'

export default {
  mixins: [AdminContext],
  name: 'image-field-vertical',
  components: {
    CropperDialog,
    HiddenFileInput,
  },
  props: {
    validator: {
      type: Object,
      default: null,
    },
    cropper: {
      type: Boolean,
      default: true,
    },
    description: {
      type: String,
      default: null,
    },
    /*
      Renders the change/delete actions as labelled buttons under a fixed-height
      thumbnail instead of icons overlaid on a full-height cover.
    */
    labelledActions: {
      type: Boolean,
      default: false,
    },
    uploadOptions: {
      type: Object,
      default: null,
    },
    errorMessages: {
      type: Object,
      default: null,
    },
    value: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      dataURLImage: null,
      uploadInProgress: false,
    }
  },
  methods: {
    removeImage() {
      this.dataURLImage = null
      this.$emit('input', null)
      this.validator && this.validator.$touch()
    },
    handleDrop(data, event) {
      event.preventDefault()
      const {
        dataTransfer: { files },
      } = event
      this.processFile(files[0])
    },
    getErrors: function (field) {
      return getValidationErrors({
        ...this.adminValidationMessages(defaultMessages),
        ...this.adminFieldValidationMessages(this.errorMessages),
      })(field)
    },
    async uploadAvatar(data) {
      this.uploadInProgress = true
      const uploadPath =
        this.uploadOptions && this.uploadOptions.path
          ? this.uploadOptions.path
          : UploadPaths.UNGROUPED
      const { file, path } = await getUploadOptions(data, uploadPath, { resize: false })
      const url = await uploadImage(file, path)
      this.$emit('input', url)
      this.uploadInProgress = false
      this.validator && this.validator.$touch()
    },
  },
}
</script>

<style scoped>
.pr-8 {
  padding-right: 8px;
}
.expand-vertical {
  height: 100% !important;
}
.fixed-height {
  height: 218px !important;
}
.field-description {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 16px;
  line-height: 1.5;
  color: #979797;
  margin-bottom: 24px;
}
.cover-actions >>> .v-btn {
  width: 100%;
  margin: 0;
}
.image-field {
  background-size: cover;
  border-radius: 8px;
  position: relative;
}
.image-container {
  padding: 0;
  border-radius: 8px;
  background-color: #f5f5f5;
}
.image-placeholder:hover {
  background-color: rgba(2, 119, 189, 0.2) !important;
}
.icon-placeholder {
  color: #0277bd;
}
.text-placeholder {
  font-family: Raleway, sans-serif;
  font-size: 18px;
  font-weight: bold;
  line-height: 1.33;
  text-align: center;
  color: #0277bd;
  margin: 8px 0 4px;
}

.image-placeholder {
  border: 2px solid #f5f5f5;
  border-radius: 8px;
  color: #fff;
  background-color: #f5f5f5;
  font-size: 14px;
  cursor: pointer;
}
.text-description {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 12px;
  line-height: 1.33;
  text-align: center;
  color: #979797;
}
.image-field::after {
  content: '';
  position: absolute;
  height: 100%;
  border-radius: 8px;
  background: rgba(33, 33, 33, 0.43);
}

.image-button {
  position: relative;
  z-index: 1;
}

.image-button >>> .v-icon {
  font-size: 20px;
}
</style>
